import { useOutletContext } from "react-router-dom";
import { useState } from "react";
import { CheckCircle2, XCircle, FileText, FileSpreadsheet } from "lucide-react";
import { useEccSrps, useEccApprove, useEccReject } from "@/hooks/useEcc";
import { useAuth } from "@/hooks/useAuth";
import type { EccArea, EccSrpSummary } from "@/api/types";

function ProposedCard({
  srp,
  onApprove,
  onReject,
  isApproving,
  isRejecting,
}: {
  srp: EccSrpSummary;
  onApprove: () => void;
  onReject: (comment: string) => void;
  isApproving: boolean;
  isRejecting: boolean;
}) {
  const [rejectOpen, setRejectOpen] = useState(false);
  const [comment, setComment] = useState("");

  return (
    <div className="rounded-lg border bg-card p-4 space-y-3">
      <div className="flex items-start gap-3">
        {srp.output_format === "docx" ? (
          <FileText className="mt-0.5 size-5 shrink-0 text-blue-500" />
        ) : (
          <FileSpreadsheet className="mt-0.5 size-5 shrink-0 text-emerald-500" />
        )}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">{srp.name}</div>
          <div className="text-xs text-muted-foreground">{srp.description}</div>
          {srp.proposed_by && (
            <div className="mt-1 text-xs text-muted-foreground">
              Propuesto por <span className="font-medium">{srp.proposed_by}</span>
            </div>
          )}
        </div>
      </div>

      {!rejectOpen && (
        <div className="flex gap-2">
          <button
            type="button"
            disabled={isApproving}
            onClick={onApprove}
            className="flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-emerald-700 disabled:opacity-60"
          >
            <CheckCircle2 className="size-4" />
            {isApproving ? "Aprobando…" : "Aprobar"}
          </button>
          <button
            type="button"
            onClick={() => setRejectOpen(true)}
            className="flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium text-destructive transition-colors hover:bg-destructive/10"
          >
            <XCircle className="size-4" /> Rechazar
          </button>
        </div>
      )}

      {rejectOpen && (
        <div className="space-y-2">
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Motivo del rechazo (requerido)…"
            rows={2}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <div className="flex gap-2">
            <button
              type="button"
              disabled={!comment.trim() || isRejecting}
              onClick={() => onReject(comment.trim())}
              className="rounded-md bg-destructive px-3 py-1.5 text-sm font-medium text-destructive-foreground disabled:opacity-60"
            >
              {isRejecting ? "Rechazando…" : "Confirmar rechazo"}
            </button>
            <button
              type="button"
              onClick={() => { setRejectOpen(false); setComment(""); }}
              className="rounded-md border px-3 py-1.5 text-sm font-medium"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function EccQueuePage() {
  const { area } = useOutletContext<{ area: EccArea }>();
  const { user } = useAuth();
  const { data, isPending, error } = useEccSrps(area);
  const approveMutation = useEccApprove();
  const rejectMutation = useEccReject();

  const proposed = (data?.items ?? []).filter((s) => s.status === "proposed");

  const canApprove = user?.role === "admin";

  if (!canApprove) {
    return (
      <div className="rounded-md border bg-card p-8 text-center text-sm text-muted-foreground">
        No tienes permiso para acceder a la cola de aprobación.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-400">
        Estás actuando como aprobador. Cada decisión queda registrada en el audit log.
      </div>

      {isPending && !data && (
        <div className="space-y-2">
          {[1, 2].map((i) => (
            <div key={i} className="h-28 animate-pulse rounded-lg border bg-muted" />
          ))}
        </div>
      )}

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          Error al cargar la cola. El backend ECC aún no está activo.
        </div>
      )}

      {!isPending && proposed.length === 0 && !error && (
        <div className="rounded-md border bg-card p-8 text-center text-sm text-muted-foreground">
          No hay SRPs pendientes de aprobación para{" "}
          <span className="font-medium">{area.toUpperCase()}</span>.
        </div>
      )}

      <div className="space-y-3">
        {proposed.map((srp) => (
          <ProposedCard
            key={srp.id}
            srp={srp}
            onApprove={() => approveMutation.mutate(srp.id)}
            onReject={(comment) => rejectMutation.mutate({ srpId: srp.id, comment })}
            isApproving={approveMutation.isPending}
            isRejecting={rejectMutation.isPending}
          />
        ))}
      </div>
    </div>
  );
}
